/**
 * 림월드식 인물 굽기(2026-10-01, 사용자 승인: "이 방식으로 모두 적용").
 *
 * 인물은 층을 겹쳐 만든다 — 몸통·머리·옷·갑옷·투구·말은 방향마다 한 장, 무기는 끝이 오른쪽인 한 장
 * (`public/sprites/rim.png`, 위치표 `rim-manifest.ts`, 원본과 도구는 `art/tools/rim_pack.py`). 팔다리가 없으니 걷기·공격·
 * 피격·쓰러짐은 그림이 아니라 **규칙**이다: 몸을 까딱이고, 무기를 내지르거나 휘두르고, 밀리고, 눕는다.
 *
 * 그 규칙을 출정할 때 차림(`Look`)마다 한 번 **구워** 지금 렌더러가 쓰는 프레임 묶음(`UnitFrames`)과 같은 모양으로 넘긴다.
 * 그래서 렌더러의 프레임 재생(걷기 박자·피격·쓰러짐·공격 타이밍·방패 손)은 그대로다. 굽기는 64칸 캔버스에 정수 자리로
 * 붙이기만 한다(무기 찌르기는 90° 단위로만 돌린다 — 도트가 깨지지 않는다. 휘두르기만 임의 각도).
 *
 * 렌더링 전용이다. 시뮬레이션은 이 파일을 모른다.
 */

export interface RimCellLayer {
  /** 방향마다(정면·왼쪽·오른쪽·뒷면) 아틀라스의 64칸 왼쪽 위. 칸 안의 자리는 이미 잡혀 있다. */
  cells: [number, number][];
  /** 말만: 방향마다 사람을 얹을 자리(칸 픽셀 이동)와, 말을 사람보다 나중에 그리는지(정면은 말 머리가 사람 앞). */
  rider?: [number, number][];
  front?: boolean[];
}

export interface RimWeaponLayer {
  /** 아틀라스 안의 무기 그림(끝이 오른쪽). */
  rect: [number, number, number, number];
  /** 쥔 자리(그림 안 픽셀). */
  grip: [number, number];
  /** 손 앞으로 나온 길이(칸 픽셀, 무기 사거리에서). */
  reach: number;
  family: 'thrust' | 'swing' | 'shoot';
  placeholder?: boolean;
}

export interface RimManifest {
  cell: number;
  feet: number;
  /** 방향마다 무기 쥔 손(칸 픽셀). */
  hands: [number, number][];
  layers: Record<string, RimCellLayer | RimWeaponLayer>;
}

/** 한 인물의 차림. 층 id(`rim-manifest.ts`의 `layers` 열쇠). 없는 층은 건너뛴다. */
export interface Look {
  head: string;
  cloth?: string;
  armor?: string;
  helm?: string;
  weapon: string;
  horse?: string;
}

export function lookKey(l: Look): string {
  return [l.head, l.cloth ?? '', l.armor ?? '', l.helm ?? '', l.weapon, l.horse ?? ''].join(',');
}

export function parseLook(key: string): Look {
  const [head, cloth, armor, helm, weapon, horse] = key.split(',');
  return { head, cloth: cloth || undefined, armor: armor || undefined, helm: helm || undefined, weapon, horse: horse || undefined };
}

/** 굽는 칸의 순서. 서기 4 · 걷기 A 4 · 걷기 B 4 · 피격 · 피격 복귀 · 쓰러짐 1–3 · 공격 [방향 4 × 칸 4]. */
export const RIM_FRAMES = 17 + 16;
export const RIM_ATTACK_FRAMES = 4;
/**
 * 구운 칸 위의 여백(픽셀). 구운 칸은 가로 64 × 세로 64 + RIM_TOP이고 발 줄은 그만큼 아래(man.feet + RIM_TOP)에 있다.
 * 64칸에 말(키 48)과 그 위에 앉은 사람을 넣었더니 기수의 투구 정수리가, 관·금관은 장식 절반이 칸 위로 잘렸다(리뷰·QA가
 * 짚었다). 렌더러는 발 줄 비율(`footAnchor`)로 세우므로 칸 높이가 달라도 자리는 그대로다.
 */
export const RIM_TOP = 16;

/** 공격 한 칸의 시간(ms). 4칸 × 95 = 0.38초 — 가장 빠른 쿨다운(21틱 = 0.7초) 안에 끝난다. */
export const RIM_ATTACK_MS = 95;
/** 치는 순간에 보일 칸(준비 0 → **타격 1** → 유지 2 → 거둠 3). 준비 칸은 치기 직전에 렌더러가 미리 보인다. */
export const RIM_ATTACK_HIT = 1;

const FACE = [Math.PI / 2, Math.PI, 0, -Math.PI / 2]; // 정면(아래) · 왼쪽 · 오른쪽 · 뒷면(위)
const VEC: [number, number][] = [
  [0, 1],
  [-1, 0],
  [1, 0],
  [0, -1],
];
const UPRIGHT = -Math.PI / 2;

/**
 * **말 위에서 무기를 쉬어 드는 각**(도, 방향마다 — 정면·왼·오른·뒷면). 세워 든 각(−90°)에서 기울인 만큼이다.
 * 걷는 사람처럼 똑바로 세우면 말 위에서는 자루가 몸과 말에 겹치고 날은 머리 위 몇 픽셀뿐이라, 창인지 칼인지가 공격하는
 * 순간에만 보였다(QA, 2026-10-07 — 적 대장마다 무기를 달리 골라 줬는데 서 있으면 다 같은 막대였다). 플레이한 사람이
 * 정했다: 말 탄 인물은 비스듬히 든다. 옆모습은 바라보는 쪽으로 기울여 끝이 말 머리 위로 나가고, 정면·뒷면은 기울일
 * 앞이 화면에 없으므로 무기 손의 바깥쪽으로 벌린다. 창(찌르기)은 조금, 칼(휘두르기)은 짧아서 더 눕혀야 날이 몸 밖으로 나온다.
 * 활은 원래 바라보는 쪽으로 든다 — 그대로다. 그림뿐이다: 치는 때와 사거리는 시뮬레이션의 것이다.
 */
export const MOUNTED_REST_DEG: Readonly<Record<'thrust' | 'swing', readonly number[]>> = {
  thrust: [-25, -35, 35, 25],
  swing: [-40, -50, 50, 40],
};

export interface Pose {
  /** 몸 전체(사람·무기·말) 이동. */
  ox?: number;
  oy?: number;
  /** 무기 각(없으면 쉬는 각 — 걷는 사람은 세워 들고 말 탄 사람은 비스듬히, `MOUNTED_REST_DEG`)과 바라보는 쪽으로 내민 길이. */
  ang?: number;
  push?: number;
  /** 눕기(쓰러짐). */
  lie?: boolean;
}

function isCell(l: RimCellLayer | RimWeaponLayer | undefined): l is RimCellLayer {
  return l !== undefined && 'cells' in l;
}

function isWeapon(l: RimCellLayer | RimWeaponLayer | undefined): l is RimWeaponLayer {
  return l !== undefined && 'rect' in l;
}

/**
 * 휘두르기: 바라보는 쪽을 가운데로 한 각(도), 칸마다(준비·타격·유지·거둠). 정면·옆은 무기를 몸 앞에 그려 어느 각이든 보인다.
 * 뒷면은 무기를 몸 **뒤**에 그리고 무기 손이 그림 오른쪽(x 39)이라, 몸을 가로질러 왼쪽을 가리키는 각(−110°~−210°)은 날이
 * 머리·몸통에 가려진다. 처음에는 뒷면도 같은 호(−100 → −20 → +40 → +10, 실제 −190° → −110° → …)라 준비·타격 칸에
 * 도끼 자루만 보였다(QA가 짚었다). 뒷면은 무기 손 쪽 아래에서 들어 올려 위(과녁 쪽)로 치는 거울 호다(+30° → −60° → −80° →
 * −40°). 유지 칸을 −100°로 더 넘겼더니 무기가 머리 뒤에 서서 몸 밖으로 1~2픽셀만 나왔다(재QA) — −80°에서 멈춘다.
 */
export const SWING_DEG: readonly (readonly number[])[] = [
  [-100, -20, 40, 10],
  [-100, -20, 40, 10],
  [-100, -20, 40, 10],
  [120, 30, 10, 50],
];

/**
 * **말 위의 휘두르기**(옆모습만 다르다 — 정면·뒷면은 `SWING_DEG` 그대로). 말 탄 사람은 칼을 비스듬히 들고 선다
 * (`MOUNTED_REST_DEG`, 옆모습은 앞으로 50° 기운 −40°·−140°). 걷는 사람의 호를 그대로 쓰면 준비 칸이 그 사선에서 거의
 * 수직(오른쪽 −100°)으로, 왼쪽은 아래(80°)로 한 칸에 꺾였다(QA, 2026-10-07 — 칼·용봉·환두대도에서 눈에 띄었다).
 * 준비를 쉬는 각에서 30° 뒤로 당긴 자리(−70°)에 두고 거기서 내려친다. 왼쪽은 오른쪽의 거울이다 — 걷는 사람의 왼쪽은
 * 같은 숫자를 써서 아래에서 올려 치지만, 말 위에서는 쉬는 칼이 이미 위에 있어 내려치는 쪽이 이어진다.
 */
export const MOUNTED_SWING_DEG: readonly (readonly number[])[] = [
  SWING_DEG[0],
  [70, 20, -40, -10],
  [-70, -20, 40, 10],
  SWING_DEG[3],
];

/** 공격 한 칸의 자세. 무기 종류(찌르기·휘두르기·쏘기)가 정한다. `mounted`: 말을 탔는가(휘두르기의 호가 다르다). */
export function attackPose(family: RimWeaponLayer['family'], dir: number, f: number, mounted = false): Pose {
  const [vx, vy] = VEC[dir];
  const lean = [-1, 1, 1, 0][f];
  const body = { ox: vx * lean, oy: vy * lean };
  if (family === 'swing') return { ...body, ang: FACE[dir] + ((mounted ? MOUNTED_SWING_DEG : SWING_DEG)[dir][f] * Math.PI) / 180 };
  if (family === 'shoot') {
    // 활을 바라보는 쪽으로 들어(준비) 쏘고(시위가 튀며 1칸 물러남) 잠깐 겨눈 뒤 내린다.
    return { ...body, ox: -vx * [0, 1, 0, 0][f], oy: -vy * [0, 1, 0, 0][f], ang: FACE[dir] + (f === 3 ? -Math.PI / 4 : 0) };
  }
  // 찌르기: 무기를 바라보는 쪽으로 눕혀(90° 단위) 준비 −3 → 타격 +4 → 유지 +4 → 거둠 +1.
  return { ...body, ang: FACE[dir], push: [-3, 4, 4, 1][f] };
}

/**
 * 차림 하나를 구워 캔버스 한 장(가로 `RIM_FRAMES`칸)으로 돌려준다. 칸 순서는 `RIM_FRAMES`의 주석.
 */
export function bakeLook(atlas: CanvasImageSource, man: RimManifest, look: Look): HTMLCanvasElement {
  const N = man.cell;
  const out = document.createElement('canvas');
  out.width = N * RIM_FRAMES;
  out.height = N + RIM_TOP;
  const g = out.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  const person = document.createElement('canvas');
  person.width = N;
  person.height = N;
  const pg = person.getContext('2d')!;
  pg.imageSmoothingEnabled = false;
  const group = document.createElement('canvas');
  group.width = N;
  group.height = N + RIM_TOP;
  const gg = group.getContext('2d')!;
  gg.imageSmoothingEnabled = false;

  const layer = (ctx: CanvasRenderingContext2D, id: string | undefined, dir: number, dx = 0, dy = 0): void => {
    const l = id ? man.layers[id] : undefined;
    if (!isCell(l)) return;
    const [sx, sy] = l.cells[dir];
    ctx.drawImage(atlas, sx, sy, N, N, dx, dy, N, N);
  };
  const weapon = isWeapon(man.layers[look.weapon]) ? (man.layers[look.weapon] as RimWeaponLayer) : null;
  const horse = isCell(man.layers[look.horse ?? '']) ? (man.layers[look.horse!] as RimCellLayer) : null;

  const drawWeapon = (ctx: CanvasRenderingContext2D, dir: number, p: Pose): void => {
    if (!weapon) return;
    const [hx, hy] = man.hands[dir];
    // 쉬는 각(서기·걷기·피격·쓰러짐): 걷는 사람의 창·칼은 세워 들고(끝이 위) 말 탄 사람은 비스듬히, 활은 바라보는 쪽으로 세워 든다 — 활 그림은 끝이 오른쪽(활등이 바라보는 쪽)이라
    // 창처럼 세우면 활이 가로로 누웠다.
    const rest =
      weapon.family === 'shoot'
        ? FACE[dir]
        : horse
          ? UPRIGHT + (MOUNTED_REST_DEG[weapon.family][dir] * Math.PI) / 180
          : UPRIGHT;
    const ang = p.ang ?? rest;
    const push = p.push ?? 0;
    const [rx, ry, rw, rh] = weapon.rect;
    ctx.save();
    ctx.translate(hx + Math.round(Math.cos(ang) * push) + 0.5, hy + Math.round(Math.sin(ang) * push) + 0.5);
    ctx.rotate(ang);
    ctx.drawImage(atlas, rx, ry, rw, rh, -weapon.grip[0] - 0.5, -weapon.grip[1] - 0.5, rw, rh);
    ctx.restore();
  };

  const cell = (slot: number, dir: number, p: Pose): void => {
    // 사람(뒷면이면 무기가 몸 뒤) → 말에 얹기 → 칸에.
    pg.clearRect(0, 0, N, N);
    if (dir === 3) drawWeapon(pg, dir, p);
    layer(pg, 'body', dir);
    layer(pg, look.cloth, dir);
    layer(pg, look.armor, dir);
    layer(pg, look.head, dir);
    layer(pg, look.helm, dir);
    if (dir !== 3) drawWeapon(pg, dir, p);
    // group은 칸 위에 RIM_TOP 여백을 둔 64 × (64 + RIM_TOP). 층은 모두 여백 아래(y = RIM_TOP)에 그린다.
    gg.clearRect(0, 0, N, N + RIM_TOP);
    if (horse) {
      const [rdx, rdy] = horse.rider![dir];
      if (horse.front![dir]) {
        gg.drawImage(person, rdx, rdy + RIM_TOP);
        layer(gg, look.horse, dir, 0, RIM_TOP);
      } else {
        layer(gg, look.horse, dir, 0, RIM_TOP);
        gg.drawImage(person, rdx, rdy + RIM_TOP);
      }
    } else gg.drawImage(person, 0, RIM_TOP);
    const x0 = slot * N;
    const ox = p.ox ?? 0;
    const oy = p.oy ?? 0;
    g.save();
    g.beginPath();
    g.rect(x0, 0, N, N + RIM_TOP);
    g.clip();
    if (p.lie) {
      // 눕기: 발 자리를 축으로 90°(도트가 그대로다). 왼쪽 보기는 왼쪽으로, 나머지는 오른쪽으로 눕는다.
      g.translate(x0 + N / 2, man.feet + RIM_TOP);
      g.rotate(dir === 1 ? -Math.PI / 2 : Math.PI / 2);
      g.drawImage(group, -N / 2 + ox, -(man.feet + RIM_TOP) + oy + 4);
    } else {
      g.drawImage(group, x0 + ox, oy);
    }
    g.restore();
  };

  for (let d = 0; d < 4; d++) {
    cell(d, d, {});
    // 걷기: 게임 박자(서기·A·서기·B)의 A·B에서 1칸 들린다.
    cell(4 + d, d, { oy: -1 });
    cell(8 + d, d, { oy: -1 });
  }
  // 피격·쓰러짐은 정면 그림(렌더러가 그렇게 쓴다). 맞은 쪽 반대로 밀린다.
  cell(12, 0, { oy: -2 });
  cell(13, 0, { oy: -1 });
  cell(14, 0, { oy: -2 });
  cell(15, 0, { lie: true });
  cell(16, 0, { lie: true });
  const fam = weapon?.family ?? 'swing';
  for (let d = 0; d < 4; d++) for (let f = 0; f < RIM_ATTACK_FRAMES; f++) cell(17 + d * 4 + f, d, attackPose(fam, d, f, horse !== null));
  return out;
}

/** 방패 손(무기를 안 든 손): 방향마다 칸 픽셀. 정면은 그림 오른쪽, 뒷면은 그림 왼쪽, 옆은 몸통 뒤쪽. */
export function shieldHands(man: RimManifest): [number, number][] {
  const [w0, w1, w2, w3] = man.hands;
  const N = man.cell;
  return [
    [N - 1 - w0[0], w0[1]],
    [w1[0] + 6, w1[1]],
    [w2[0] - 6, w2[1]],
    [N - 1 - w3[0], w3[1]],
  ];
}

/** 방패 손 자리: 발 자리(칸 가운데, 발 아래 줄)에서 잰 칸 픽셀. 렌더러의 `UnitFrames.hands`와 같은 꼴. */
export interface LookHands {
  stand: { dx: number; dy: number }[];
  walkA: { dx: number; dy: number }[];
  walkB: { dx: number; dy: number }[];
}

/**
 * 차림의 방패 손 자리(방향마다, 서기·걷기 A·B). 말을 탔으면 `bakeLook`이 사람을 말의 `rider` 만큼 옮겨 그리므로 손도 같이
 * 옮긴다. 처음에는 손 자리를 모든 차림이 함께 썼더니 말 탄 장수·기병의 방패가 걸어 다니는 사람의 손 높이, 곧 말 배와 다리
 * 사이에 붙었다(사용자 화면). 걷기 칸은 `bakeLook`이 한 칸 들어 그린다.
 */
export function lookHands(man: RimManifest, horse?: string): LookHands {
  const n = man.cell;
  const h = horse ? man.layers[horse] : undefined;
  const rider = isCell(h) && h.rider ? h.rider : null;
  const shields = shieldHands(man);
  const at = (d: number, dy: number): { dx: number; dy: number } => {
    const [x, y] = shields[d];
    const [rx, ry] = rider ? rider[d] : [0, 0];
    return { dx: Math.round(x + 0.5 - n / 2) + rx, dy: Math.round(y + 0.5 - (man.feet + 1)) + ry + dy };
  };
  return {
    stand: [0, 1, 2, 3].map((d) => at(d, 0)),
    walkA: [0, 1, 2, 3].map((d) => at(d, -1)),
    walkB: [0, 1, 2, 3].map((d) => at(d, -1)),
  };
}
